import { css } from "@emotion/css";
import { Callout, ExternalLink, InlineMessage, useShowSnackbar } from "@octopusdeploy/design-system-components";
import { ArrowRotateIcon } from "@octopusdeploy/design-system-icons";
import { space, themeTokens } from "@octopusdeploy/design-system-tokens";
import { useQuery } from "@octopusdeploy/octopus-react-client";
import type { ActionProperties, ActionTemplateParameterResource, ActionTemplateResource, CommunityActionTemplateSnapshotResource, GitRefResource, PackageReference, ProcessTemplateParameterResource } from "@octopusdeploy/octopus-server-client";
import { flatten } from "lodash";
import pluralize from "pluralize";
import { useState } from "react";
import type { ProducerTemplateProcessContext } from "~/areas/projects/components/Process/ProjectTemplates/useProducerTemplateProcessContext";
import type { ProcessIdentifier, StoredModelState } from "~/areas/projects/components/Process/types";
import type { ProcessTemplateSourceItems } from "~/components/ActionTemplateParameterInput/ActionTemplateParameterInput";
import ActionTemplateParameterInputExpandableFormElement from "~/components/ActionTemplateParameterInput/ActionTemplateParameterInputExpandableFormElement";
import { FormSectionHeading, UnstructuredFormSection } from "~/components/form";
import { FormSection } from "~/components/form/index";
import { getUpdatedCommunityTemplateProperties } from "./getUpdatedCommunityTemplateProperties";
interface ProcessTemplateCommunityActionTemplateSectionProps {
    actionTemplate: ActionTemplateResource;
    process: Readonly<StoredModelState>;
    processIdentifier: ProcessIdentifier;
    gitRefResource: GitRefResource | undefined;
    properties: ActionProperties;
    setProperties(properties: Partial<ActionProperties>): void;
    removeProperties(propertyKeys: string[]): void;
    communityActionTemplateSnapshot: CommunityActionTemplateSnapshotResource;
    doBusyTask(action: () => Promise<void>): Promise<boolean>;
    setActionCommunityActionTemplateSnapshot(communityActionTemplateSnapshot: CommunityActionTemplateSnapshotResource): void;
    producerTemplateProcessContext: ProducerTemplateProcessContext;
    setPackages(packages: Array<PackageReference<{}>>, initialise?: boolean): void;
    onParameterAdded: ((parameters: ProcessTemplateParameterResource[]) => void) | undefined;
}
type UpdateResult = {
    added: string[];
    removed: string[];
    typeChanged: string[];
};
function ProcessTemplateCommunityActionTemplateSection({ actionTemplate, process, processIdentifier, gitRefResource, properties, setProperties, removeProperties, communityActionTemplateSnapshot, setActionCommunityActionTemplateSnapshot, doBusyTask, producerTemplateProcessContext, setPackages, onParameterAdded, }: ProcessTemplateCommunityActionTemplateSectionProps) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const { result: communityActionTemplate } = useQuery((repository) => repository.CommunityActionTemplates.get(actionTemplate.CommunityActionTemplateId!), [actionTemplate], "Get Community Action Template");
    const showSnackbar = useShowSnackbar();
    const [updateResult, setUpdateResult] = useState<UpdateResult | null>(null);
    const actionNames: string[] = process ? flatten(Object.values(process.actions.byId).map((a) => a.Name)) : [];
    const sourceItems: ProcessTemplateSourceItems = {
        stepNames: actionNames,
        processIdentifier: processIdentifier,
        gitRef: gitRefResource,
        parameters: producerTemplateProcessContext.parameters,
    };
    if (!communityActionTemplate) {
        return null;
    }
    const onUpdateCommunityActionTemplate = () => {
        const oldParameters: ActionTemplateParameterResource[] = communityActionTemplateSnapshot.Parameters;
        const oldParameterNames = oldParameters.map((p) => p.Name);
        const newParameterNames = communityActionTemplate.Parameters.map((p) => p.Name);
        const addedNames = newParameterNames.filter((name) => !oldParameterNames.includes(name));
        const removedNames = oldParameterNames.filter((name) => !newParameterNames.includes(name));
        const parameterNamesWithChangedTypes = communityActionTemplate.Parameters.filter((p) => {
            const oldParam = oldParameters.find((op) => op.Name === p.Name);
            return oldParam && oldParam.DisplaySettings["Octopus.ControlType"] !== p.DisplaySettings["Octopus.ControlType"];
        }).map((p) => p.Name);
        setUpdateResult({ added: addedNames, removed: removedNames, typeChanged: parameterNamesWithChangedTypes });
        const { propertiesToOverwrite, propertyKeysToRemove } = getUpdatedCommunityTemplateProperties(communityActionTemplate.Properties, newParameterNames, properties);
        setProperties(propertiesToOverwrite);
        removeProperties(propertyKeysToRemove);
        setActionCommunityActionTemplateSnapshot({
            Id: communityActionTemplate.Id,
            Version: communityActionTemplate.Version,
            Parameters: communityActionTemplate.Parameters,
        });
        setPackages(communityActionTemplate.Packages);
        showSnackbar("Community step template updated");
    };
    const parameters: ActionTemplateParameterResource[] = communityActionTemplateSnapshot.Parameters;
    const currentVersion = communityActionTemplateSnapshot.Version;
    const isUpdateAvailable = communityActionTemplate.Version > Number(currentVersion);
    return (<div>
            <FormSectionHeading title={actionTemplate.Name}/>
            <UnstructuredFormSection>
                <span>
                    This step is based on a community step template <ExternalLink href={communityActionTemplate.Website}>{actionTemplate.Name}</ExternalLink>.
                </span>
            </UnstructuredFormSection>
            {updateResult && (<Callout type={"information"} title={"Community step template updated successfully"} canClose={true} onClose={() => setUpdateResult(null)}>
                    <p>
                        The community step template <strong>{communityActionTemplate.Name}</strong> was successfully updated.
                    </p>
                    <ul className={styles.stepTemplateCalloutList}>
                        {updateResult.added.length > 0 ? (<li>
                                The following {pluralize("parameter", updateResult.added.length)} {pluralize("was", updateResult.added.length)} added:
                                <ul className={styles.unimportedParametersList}>
                                    {updateResult.added.map((name) => (<li key={name}>{name}</li>))}
                                </ul>
                            </li>) : null}
                        {updateResult.removed.length > 0 ? (<li>
                                The following {pluralize("parameter", updateResult.removed.length)} {pluralize("was", updateResult.removed.length)} removed:
                                <ul className={styles.unimportedParametersList}>
                                    {updateResult.removed.map((name) => (<li key={name}>{name}</li>))}
                                </ul>
                            </li>) : null}
                        {updateResult.typeChanged.length > 0 ? (<li>
                                The following {pluralize("parameter", updateResult.typeChanged.length)} {pluralize("has", updateResult.typeChanged.length)} changed types:
                                <ul className={styles.unimportedParametersList}>
                                    {updateResult.typeChanged.map((name) => (<li key={name}>{name}</li>))}
                                </ul>
                            </li>) : null}
                    </ul>
                </Callout>)}
            <FormSection title={isUpdateAvailable ? (<span className={styles.templateVersionTitle}>
                            Template version
                            <ArrowRotateIcon color={themeTokens.color.icon.info} size={20}/>
                        </span>) : ("Template version")} help={<>{currentVersion && <span>Currently using: v{currentVersion}</span>}</>} includeBorder={true}>
                {isUpdateAvailable && (<InlineMessage type="information" title="Version update:" description={`v${communityActionTemplate.Version} of ${communityActionTemplate.Name} is available.`} action={{
                label: "Update",
                onClick: onUpdateCommunityActionTemplate,
            }}/>)}
            </FormSection>

            <div>
                {parameters.map((parameter) => {
            return (<ActionTemplateParameterInputExpandableFormElement parameter={parameter} key={parameter.Name} localNames={producerTemplateProcessContext.localNames} value={properties[parameter.Name]} doBusyTask={doBusyTask} sourceItems={sourceItems} actionType={actionTemplate.ActionType} onChange={(x) => setProperties({ [parameter.Name]: x })} onParameterAdded={onParameterAdded}/>);
        })}
            </div>
        </div>);
}
const styles = {
    templateVersionTitle: css({ display: "inline-flex", alignItems: "center", gap: space[4] }),
    unimportedParametersList: css({
        listStyleType: "circle",
        listStylePosition: "inside",
        paddingLeft: space[16],
        margin: 0,
    }),
    stepTemplateCalloutList: css({
        listStyleType: "disc",
        listStylePosition: "inside",
        margin: 0,
    }),
};
export default ProcessTemplateCommunityActionTemplateSection;
